import {
    CommerceProperty,
    CommercePropertyValue,
    ProductAvailableQuantity,
    ProductsDataActions,
    ProductSearchResult,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { IGroupedItemLine, ILineItem, ProductLineCommerceProperties, ProductLineDataType } from '../utils';
import {
    ArrayExtensions,
    createInventoryAvailabilitySearchCriteria,
    DeliveryMode,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { IActionContext } from '@msdyn365-commerce/core';
import getProductDefaultVariantAction, { ProductDefaultVariantInput } from '../../actions/get-product-default-variant.action';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { IAddToCartResources } from '../../themes/memoryexpress/views/components/addtocart.component';

/**
 * Interface for linking product inventory availability to a product id
 */
export interface ProductVariantInventoryInformation {
    productId: number;
    availableQuantity: ProductAvailableQuantity;
}

/**
 * Interface containing data required for add to cart for a list of products
 */
export interface ProductListAddToCartData {
    productVariants: SimpleProduct[];
    inventoryInfo: ProductVariantInventoryInformation[];
}

/**
 * Inteface for product compoenent add to cart resources
 */
export interface IProductComponentAddToCartResources extends IAddToCartResources {
    addToCartText: string;
}

/**
 * Determines if an item line is a warranty line.
 * @param itemLine the itemLine to evaluate.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 */
export function getIsWarrantyLineFromProductLine<T extends ILineItem>(itemLine: T, lineDataType: ProductLineDataType): boolean {
    return getItemLineProperty(itemLine, lineDataType, ProductLineCommerceProperties.isWarrantyLine)?.BooleanValue ?? false;
}

/**
 * Determines if an item line is a build line.
 * @param itemLine the itemLine to evaluate.
 */
export function getIsBuildParent<T extends { ExtensionProperties?: CommerceProperty[] }>(itemLine: T): boolean {
    return getItemProperty(itemLine, ProductLineCommerceProperties.isBuildParent)?.BooleanValue ?? false;
}

/**
 * Determines if an item line is a build line.
 * @param itemLines the itemLine to evaluate.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 */
export function getIsBuildParentFromProductLine<T extends ILineItem>(itemLine: T, lineDataType: ProductLineDataType): boolean {
    return getItemLineProperty(itemLine, lineDataType, ProductLineCommerceProperties.isBuildParent)?.BooleanValue ?? false;
}

/**
 * Retreives an extension property from an item line.
 * @param itemLine the itemLine to evaluate.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 * @param commercePropName The name of the commerce property to search for.
 */
export function getItemLineProperty<T extends ILineItem>(
    itemLine: T,
    lineDataType: ProductLineDataType,
    commercePropName: ProductLineCommerceProperties
): CommercePropertyValue | undefined {
    return getItemProperty(itemLine.data[`${lineDataType}`], commercePropName);
}

/**
 * Retreives a warranty line property from an item.
 * @param itemLine the itemLine to evaluate.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 * @param commercePropName The name of the commerce property to search for.
 */
export function getItemProperty<T extends { ExtensionProperties?: CommerceProperty[] }>(
    itemData: T,
    commercePropName: ProductLineCommerceProperties
): CommercePropertyValue | undefined {
    return itemData.ExtensionProperties?.find((commerceProp: CommerceProperty) => commerceProp.Key === commercePropName)?.Value;
}

/**
 * Determines if item line number matches value provided.
 * @param value The number to compare to the provided itemLine's line number.
 * @param itemLines the itemLine to compare the value to.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 */
export function getDoesValueMatchLineNumber<T extends ILineItem>(
    value: number | undefined,
    itemLine: T,
    lineDataType: ProductLineDataType
) {
    return itemLine.data[`${lineDataType}`].LineNumber === value;
}

/**
 * Given a set of Line items, returns a list of paired line items,
 * where cart line items are paired with their associated warranties.
 * @param itemLines the list of itemLines to pair.
 * @param lineDataType the data item within the object. This should be the WarrantyLineCommerceProperty where the name matches the value of T.
 */
export function pairGroupedItemLines<T extends ILineItem>(itemLines: T[], lineDataType: ProductLineDataType): IGroupedItemLine<T>[] {
    const groupedItemLines: IGroupedItemLine<T>[] = [];
    const warrantyItemLines: T[] = [];
    const buildItemLines: T[] = [];

    let lastBuildParentLine: IGroupedItemLine<T> | undefined;
    let lastItemLine: T | undefined;

    itemLines.forEach(function(itemLine) {
        const isWarranty: boolean = getIsWarrantyLineFromProductLine<T>(itemLine, lineDataType);
        const isBuildParent: boolean =
            getItemLineProperty<T>(itemLine, lineDataType, ProductLineCommerceProperties.isBuildParent)?.BooleanValue ?? false;
        const isBuildItem: boolean =
            getItemLineProperty<T>(itemLine, lineDataType, ProductLineCommerceProperties.isBuildLine)?.BooleanValue ?? false;

        if (isWarranty) {
            if (
                lastItemLine &&
                itemLine.data &&
                getDoesValueMatchLineNumber(
                    getItemLineProperty<T>(itemLine, lineDataType, ProductLineCommerceProperties.warrantableLineNumber)?.DecimalValue,
                    lastItemLine,
                    lineDataType
                )
            ) {
                groupedItemLines.push({ item: lastItemLine, warranty: itemLine });
                lastItemLine = undefined;
            } else {
                warrantyItemLines.push(itemLine);
            }
        } else if (isBuildParent) {
            if (lastItemLine) {
                groupedItemLines.push({ item: lastItemLine, warranty: undefined });
            }
            lastItemLine = undefined;
            if (lastBuildParentLine) {
                groupedItemLines.push(lastBuildParentLine);
            }
            lastBuildParentLine = { item: itemLine };
        } else if (isBuildItem) {
            if (lastItemLine) {
                groupedItemLines.push({ item: lastItemLine, warranty: undefined });
            }
            lastItemLine = undefined;
            const buildParentLineNumberProperty = getItemLineProperty<T>(
                itemLine,
                lineDataType,
                ProductLineCommerceProperties.buildParentLineNumber
            );
            const buildParentLineNumber = buildParentLineNumberProperty?.DecimalValue ?? buildParentLineNumberProperty?.IntegerValue;
            if (lastBuildParentLine && getDoesValueMatchLineNumber(buildParentLineNumber, lastBuildParentLine.item, lineDataType)) {
                if (lastBuildParentLine.buildItems) {
                    lastBuildParentLine.buildItems.push(itemLine);
                } else {
                    lastBuildParentLine.buildItems = [itemLine];
                }
            } else {
                if (lastItemLine) {
                    groupedItemLines.push({ item: lastItemLine });
                }
                lastItemLine = undefined;
                buildItemLines.push(itemLine);
            }
        } else {
            if (lastItemLine) {
                groupedItemLines.push({ item: lastItemLine });
            }
            lastItemLine = itemLine;
        }
    });

    if (lastItemLine) {
        groupedItemLines.push({ item: lastItemLine });
        lastItemLine = undefined;
    }
    if (lastBuildParentLine) {
        groupedItemLines.push(lastBuildParentLine);
        lastBuildParentLine = undefined;
    }

    warrantyItemLines.forEach(function(warrantyItemLine) {
        if (warrantyItemLine.data) {
            const warrantableLineNumber = getItemLineProperty<T>(
                warrantyItemLine,
                lineDataType,
                ProductLineCommerceProperties.warrantableLineNumber
            );
            const warrantableItemLineIndex = groupedItemLines.findIndex(
                pairedItemLines =>
                    pairedItemLines.item.data &&
                    getDoesValueMatchLineNumber(warrantableLineNumber?.DecimalValue, pairedItemLines.item, lineDataType)
            );
            if (warrantableItemLineIndex >= 0) {
                groupedItemLines[+warrantableItemLineIndex].warranty = warrantyItemLine;
            }
        }
    });
    buildItemLines.forEach(function(itemLine) {
        let buildFound = false;
        groupedItemLines.forEach(gil => {
            const buildParentLineNumber = getItemLineProperty<T>(
                itemLine,
                lineDataType,
                ProductLineCommerceProperties.buildParentLineNumber
            )?.IntegerValue;
            if (!buildFound && getDoesValueMatchLineNumber(buildParentLineNumber, gil.item, lineDataType)) {
                buildFound = true;
                if (gil.buildItems) {
                    gil.buildItems.push(itemLine);
                } else {
                    gil.buildItems = [itemLine];
                }
            }
        });
        if (!buildFound) {
            groupedItemLines.push({ item: itemLine });
        }
    });

    return groupedItemLines;
}

/**
 * Gets data required for add to cart
 * @param products List of products to fetch
 * @param context Action context
 * @returns ProductListAddToCartData (product variants and inventory info)
 */
export async function getProductAddtoCartData(products: ProductSearchResult[], context: IActionContext): Promise<ProductListAddToCartData> {
    const productVariants: SimpleProduct[] = [];
    const inventoryInfo: ProductVariantInventoryInformation[] = [];
    if (products) {
        const defaultVariants = await getProductDefaultVariantAction(
            products.map(product => {
                return new ProductDefaultVariantInput(product.RecordId);
            }),
            context
        );

        if (defaultVariants) {
            //Get variant product information
            const variantIds = defaultVariants.map(variant => variant.DefaultProductId);
            const variants = await getByIdsAsync(
                { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
                context.requestContext.apiSettings.channelId,
                variantIds
            );
            productVariants.push(...variants);

            //Get variant availability
            const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
                context,
                [...new Set<number>(defaultVariants.map(variant => variant.DefaultProductId))],
                true,
                undefined,
                undefined,
                DeliveryMode.shipping
            );

            const availabilities = await ProductsDataActions.getEstimatedAvailabilityAsync(
                { callerContext: context },
                shippingSearchCriteria
            );
            if (availabilities?.AggregatedProductInventoryAvailabilities) {
                availabilities?.AggregatedProductInventoryAvailabilities.forEach(availability => {
                    const masterId = defaultVariants.find(variant => variant.DefaultProductId === availability.ProductId)?.MasterId;
                    if (masterId) {
                        inventoryInfo.push({
                            productId: masterId,
                            availableQuantity: {
                                ProductId: availability?.ProductId,
                                AvailableQuantity: availability?.TotalAvailableQuantity,
                                UnitOfMeasure: availability?.UnitOfMeasure
                            }
                        });
                    }
                    return;
                });
            }
        }
    }

    return { productVariants, inventoryInfo };
}

/**
 * Gets the brand attribute from a product search result if attributes are included
 * @param product to
 * @returns Text value of the brand attribute
 */
export const getBrand = (product: ProductSearchResult): string | undefined => {
    if (!ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }

    const brandAttribute = product.AttributeValues.find(attribute => attribute.Name === 'Brand');
    if (brandAttribute) {
        return brandAttribute.TextValue;
    }

    return undefined;
};
